import {ChangeDetectorRef, Component, EventEmitter, inject, OnInit, Output} from '@angular/core';
import {NZ_DRAWER_DATA, NzDrawerRef} from 'ng-zorro-antd/drawer';
import {
  ContainerAdvancedSearchDrawerData,
  ContainerAdvancedSearchRow,
  ContainerAdvancedSearchState,
  ContainerAdvancedSearchSummary
} from '../../models/container-advanced-search';
import {ContainerAdvancedSearchService} from '../../services/container-advanced-search.service';

@Component({
  selector: 'app-container-advanced-search',
  standalone: false,
  templateUrl: './container-advanced-search.component.html',
  styleUrl: './container-advanced-search.component.less'
})
export class ContainerAdvancedSearchComponent implements OnInit {
  data: ContainerAdvancedSearchDrawerData = inject(NZ_DRAWER_DATA);

  @Output() applyFound = new EventEmitter<string[]>();
  @Output() clearAppliedRequest = new EventEmitter<void>();
  @Output() stateChanged = new EventEmitter<ContainerAdvancedSearchState>();

  searchInput = '';
  loading = false;
  rows: ContainerAdvancedSearchRow[] = [];
  summary: ContainerAdvancedSearchSummary = {
    total: 0,
    found: 0,
    notFound: 0,
    invalid: 0,
  };
  searched = false;

  constructor(
    private advancedSearchService: ContainerAdvancedSearchService,
    private cdr: ChangeDetectorRef,
    private drawerRef: NzDrawerRef<string>,
  ) {}

  ngOnInit(): void {
    if (!this.data.initialState) {
      return;
    }

    this.searchInput = this.data.initialState.searchInput;
    this.rows = [...this.data.initialState.rows];
    this.summary = {...this.data.initialState.summary};
    this.searched = this.data.initialState.searched;
  }

  onSearchInputChange(value: string): void {
    this.searchInput = value;
    this.emitState();
  }

  close(): void {
    this.drawerRef.close();
  }

  async runSearch(): Promise<void> {
    const normalizedContainers = this.advancedSearchService.parseAndNormalize(this.searchInput);
    this.searched = true;

    if (normalizedContainers.length === 0) {
      this.rows = [];
      this.summary = this.advancedSearchService.buildSummary(normalizedContainers, []);
      this.emitState();
      return;
    }

    this.loading = true;
    try {
      const matches = normalizedContainers.length > 0
        ? await this.data.resolveMatches(normalizedContainers)
        : [];

      this.rows = this.advancedSearchService.buildRows(normalizedContainers, matches);
      this.summary = this.advancedSearchService.buildSummary(normalizedContainers, matches);
      this.emitState();
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  requestClearAppliedFilter(): void {
    if (this.loading) {
      return;
    }
    this.clearAppliedRequest.emit();
  }

  resetState(): void {
    this.searchInput = '';
    this.rows = [];
    this.searched = false;
    this.summary = this.advancedSearchService.buildSummary([], []);
    this.emitState();
    this.cdr.detectChanges();
  }

  exportCsv(): void {
    if (this.rows.length === 0) {
      return;
    }
    this.advancedSearchService.exportToCsv(this.rows, 'busqueda-contenedores.csv', this.data.section);
  }

  applyFoundRows(): void {
    const foundContainerRefs = [...new Set(
      this.rows
        .filter(row => row.classification === 'found')
        .map(row => row.containerNumber)
    )];
    this.applyFound.emit(foundContainerRefs);
  }

  get hasFoundRows(): boolean {
    return this.rows.some(row => row.classification === 'found');
  }

  getClassificationLabel(classification: ContainerAdvancedSearchRow['classification']): string {
    if (classification === 'found') {
      return 'Encontrado';
    }
    if (classification === 'not_found') {
      return 'No encontrado';
    }
    return 'Inválido';
  }

  private emitState(): void {
    this.stateChanged.emit({
      searchInput: this.searchInput,
      rows: [...this.rows],
      summary: {...this.summary},
      searched: this.searched,
    });
  }
}
